import React from "react";

import Bars from "./charts/Bars";
import Heatmap from "./charts/Heatmap";
import Radar from "./charts/Radar";
import Ring from "./charts/Ring";
import Sparkline from "./charts/Sparkline";
import { Empty, Kpi, Legend, MeterRow, Section, TableScroll, Td, Th } from "./primitives";
import {
  NOT_RECORDED,
  STATE_COLORS,
  WELLNESS_STATES,
  acwrColor,
  deltaColor,
  fmtDayNumber,
  fmtDelta,
  fmtNumber,
  fmtPercent,
  fmtPeriod,
  fmtShortDate,
} from "./reportFormat";

/**
 * Sections of the individual report — the dossier a coach prints for a family
 * meeting, or a club sends to a scout.
 *
 * Which sections appear is decided by the server per audience. The scout
 * variant genuinely has no wellness or nutrition data to render, so those
 * sections are absent rather than blanked.
 */

// --- profile header ----------------------------------------------------------

const Chip = ({ children, color }) => (
  <span
    className="rounded-full border px-3 py-1 text-[12.5px]"
    style={
      color
        ? { background: color, borderColor: color, color: "#fff" }
        : { background: "#16336d", borderColor: "#2351ac", color: "#d7dff2" }
    }
  >
    {children}
  </span>
);

export const ProfileSection = ({ report }) => {
  const a = report.athlete;
  const internal = report.audience === "coach" || report.audience === "coordinacion";

  return (
    <Section tone="dark">
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <h1 className="font-display text-[46px] leading-none font-semibold text-white">
            {a.full_name}
          </h1>
          <p className="mt-1.5 text-sm text-brand-200">
            {[a.club, a.category, a.position].filter(Boolean).join(" · ") || "Sin datos de club"}
          </p>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {internal && a.state ? (
              <Chip color={a.state.color}>
                {a.state.name} · {fmtPercent(a.score)}
              </Chip>
            ) : (
              a.availability && <Chip color={a.availability.color}>{a.availability.label}</Chip>
            )}
            <Chip>
              Índice deportivo{" "}
              {a.performance_index === null ? "sin datos" : `${a.performance_index}/100`}
            </Chip>
            <Chip>{a.sessions_recorded} días evaluados</Chip>
          </div>
        </div>

        <dl className="grid grid-cols-[auto_1fr] content-start gap-x-3.5 gap-y-1.5 text-[13px]">
          {[
            ["Código", a.athlete_id.slice(0, 8)],
            ["Edad", a.age === null ? NOT_RECORDED : `${a.age} años`],
            [
              "Altura / peso",
              a.height || a.weight
                ? `${a.height ? `${a.height} cm` : NOT_RECORDED} · ${a.weight ? `${a.weight} kg` : NOT_RECORDED}`
                : NOT_RECORDED,
            ],
            ["Deporte", a.sport || NOT_RECORDED],
            ["Periodo", fmtPeriod(report.period)],
            ["Vista", report.audience_name],
          ].map(([term, value]) => (
            <React.Fragment key={term}>
              <dt className="text-peri-200">{term}</dt>
              <dd className="m-0 text-right">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    </Section>
  );
};

// --- state / availability ----------------------------------------------------

export const StateSection = ({ report }) => {
  const a = report.athlete;

  return (
    <Section
      title="Estado"
      meta="Promedio de los últimos 3 check-in · sueño, carga, recuperación y ánimo"
    >
      {a.score === null ? (
        <Empty>
          {a.full_name} no registró su estado en el periodo. El índice se calcula desde el check-in
          diario de la app: sin registros no hay estado que reportar.
        </Empty>
      ) : (
        <div className="grid items-center gap-6 lg:grid-cols-[250px_1fr]">
          <div className="mx-auto w-full max-w-[220px]">
            <Ring value={a.score} color={a.state?.color} caption={a.state?.name} />
          </div>
          <div>
            <p className="max-w-[62ch] text-sm text-ink">
              {a.availability?.detail}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <Sparkline
                values={a.score_series}
                width={160}
                height={34}
                label={`Índice de estado de ${a.full_name} en los últimos 14 días`}
              />
              <span className="text-[12.5px] text-ink">
                Cambio en el periodo:{" "}
                <b style={{ color: deltaColor(a.score_trend) }}>{fmtDelta(a.score_trend)}</b>
              </span>
            </div>
            <p className="mt-2 text-[11.5px] text-peri-300">
              Adherencia al check-in diario: {fmtPercent(a.checkin_adherence)} de los{" "}
              {report.period.days} días del periodo.
            </p>
          </div>
        </div>
      )}
    </Section>
  );
};

export const AvailabilitySection = ({ report }) => {
  const a = report.athlete;

  return (
    <Section title="Disponibilidad" meta="Determinada por el control de carga del club">
      <div className="grid items-center gap-6 lg:grid-cols-[250px_1fr]">
        <div className="mx-auto w-full max-w-[220px]">
          <Ring value={a.performance_index} caption="índice deportivo" suffix="" />
        </div>
        <div>
          {a.availability ? (
            <>
              <p className="font-display text-[26px] font-semibold" style={{ color: a.availability.color }}>
                {a.availability.label}
              </p>
              <p className="mt-1.5 max-w-[62ch] text-sm text-ink">{a.availability.detail}</p>
            </>
          ) : (
            <p className="text-sm text-ink">
              El club aún no tiene registros suficientes para declarar disponibilidad.
            </p>
          )}
          <p className="mt-3.5 text-[13.5px] text-ink">
            Índice deportivo {a.performance_index ?? NOT_RECORDED}/100
            {a.performance_delta !== null &&
              `, ${a.performance_delta >= 0 ? "con una mejora" : "con una caída"} de ${Math.abs(a.performance_delta)} puntos dentro del periodo`}
            , sobre {a.sessions_recorded} días evaluados por el cuerpo técnico.
          </p>
          <p className="mt-2.5 text-[11.5px] text-peri-300">
            Esta vista no incluye información de salud del deportista.
          </p>
        </div>
      </div>
    </Section>
  );
};

// --- performance -------------------------------------------------------------

export const PerformanceSection = ({ report }) => {
  const a = report.athlete;
  const metrics = a.metrics;

  return (
    <Section
      title="Métricas de sesión"
      meta={
        <>
          Promedio del deportista frente al promedio de {report.peers.category}
          <br />
          {report.peers.count} deportistas comparados
        </>
      }
    >
      {metrics.length === 0 ? (
        <Empty>
          Sin métricas de entrenamiento registradas en el periodo. Se capturan desde el perfil del
          deportista en la app.
        </Empty>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <Radar
              values={metrics.map((m) => m.normalized ?? 0)}
              reference={metrics.map((m) => m.group_normalized ?? 0)}
              labels={metrics.map((m) => m.title)}
            />
            <Legend
              items={[
                { label: a.full_name.split(" ")[0], color: "#2351ac" },
                { label: `Promedio ${report.peers.category}`, color: "#aebfe4" },
              ]}
            />
          </div>

          <div>
            <TableScroll minWidth={520}>
              <table className="w-full border-collapse text-[13.5px]">
                <thead>
                  <tr>
                    <Th>Métrica</Th>
                    <Th align="right">Última</Th>
                    <Th align="right">Prom.</Th>
                    <Th align="right">Mejor</Th>
                    <Th align="right">Cambio</Th>
                    <Th>Evolución</Th>
                    <Th>Percentil</Th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((metric) => {
                    const decimals = metric.unit === "%" ? 0 : 1;
                    return (
                      <tr key={metric.key}>
                        <Td>{metric.title}</Td>
                        <Td align="right" className="font-display text-[19px]">
                          {fmtNumber(metric.latest, decimals)}
                          <span className="text-[12px] text-peri-300"> {metric.unit}</span>
                        </Td>
                        <Td align="right">{fmtNumber(metric.average, decimals)}</Td>
                        <Td align="right">{fmtNumber(metric.best, decimals)}</Td>
                        <Td align="right" style={{ color: deltaColor(metric.delta) }}>
                          {fmtDelta(metric.delta, decimals)}
                        </Td>
                        <Td>
                          <Sparkline
                            values={metric.series.map((point) => point.value)}
                            label={`Evolución de ${metric.title}`}
                          />
                        </Td>
                        <Td>
                          {metric.percentile === null ? (
                            <span className="text-peri-300">{NOT_RECORDED}</span>
                          ) : (
                            <div className="flex items-center gap-2 text-[12px] text-ink">
                              <span className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-peri-100">
                                <span
                                  className="block h-full bg-brand-400"
                                  style={{ width: `${metric.percentile}%` }}
                                />
                              </span>
                              P{metric.percentile}
                            </div>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
            <p className="mt-2.5 text-[11.5px] leading-relaxed text-peri-300">
              El cambio compara la segunda mitad del periodo contra la primera. El percentil sitúa
              al deportista dentro de su categoría, no contra un estándar absoluto.
            </p>
          </div>
        </div>
      )}
    </Section>
  );
};

// --- training load -----------------------------------------------------------

export const LoadSection = ({ report }) => {
  const a = report.athlete;
  const weekly = report.weekly_load;
  const average = weekly.reduce((acc, w) => acc + w.load, 0) / (weekly.length || 1);

  return (
    <Section title="Carga de entrenamiento" meta="Esfuerzo percibido × duración de la sesión">
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Kpi
          label="Carga última semana"
          value={fmtNumber(a.load_7d)}
          unit="UA"
          note="Suma de RPE × minutos"
        />
        <Kpi
          label="Ratio aguda:crónica"
          value={fmtNumber(a.acwr, 2)}
          note="Zona recomendada 0.8 – 1.3"
          color={acwrColor(a.acwr)}
        />
        <Kpi
          label="Monotonía"
          value={fmtNumber(a.monotony, 2)}
          note="Sobre 2.0 la semana es demasiado uniforme"
        />
        <Kpi
          label="RPE promedio"
          value={fmtNumber(a.wellness?.rpe ?? null, 1)}
          unit="/10"
          note={`${a.sessions_recorded} días con registro`}
        />
      </div>

      <div className="mt-4 max-w-[460px]">
        <p className="mb-1 text-[11.5px] text-peri-300">Carga semanal (últimas 4 semanas)</p>
        <Bars
          values={weekly.map((w) => w.load)}
          labels={weekly.map((w) => w.label)}
          reference={average * 1.5}
          referenceLabel="150% del promedio"
          label="Carga semanal de las últimas cuatro semanas"
        />
        <p className="text-[11.5px] leading-relaxed text-peri-300">
          La línea punteada marca el 150% del promedio: por encima, el salto de carga es el
          principal predictor de lesión.
        </p>
      </div>
    </Section>
  );
};

// --- nutrition ---------------------------------------------------------------

export const NutritionSection = ({ report }) => {
  const n = report.athlete.nutrition;
  if (!n) return null;

  const reference = n.reference_calories;
  const logged = n.daily_calories.filter((d) => d.calories !== null && d.calories > 0);

  return (
    <Section title="Nutrición" meta={`Comidas registradas en la app · últimos ${n.window_days} días`}>
      {logged.length === 0 ? (
        <Empty>
          Ninguna comida registrada en los últimos {n.window_days} días. Sin ese dato el reporte
          nutricional no puede sostenerse.
        </Empty>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
            <Kpi
              label="Ingesta promedio"
              value={fmtNumber(n.avg_calories)}
              unit="kcal"
              note={reference ? `Referencia estimada: ${fmtNumber(reference)} kcal/día` : "Sin edad registrada para estimar referencia"}
              color={
                reference && n.avg_calories !== null && n.avg_calories < reference * 0.85
                  ? STATE_COLORS.warn
                  : undefined
              }
            />
            <Kpi
              label="Días completos"
              value={`${n.complete_days}`}
              unit={`/${n.window_days}`}
              note="Con desayuno, almuerzo y cena"
              color={n.complete_days >= 5 ? STATE_COLORS.ok : STATE_COLORS.warn}
            />
            <Kpi
              label="Desayuno registrado"
              value={`${n.breakfast_days}`}
              unit={`/${n.window_days}`}
              note="Comida más ligada al rendimiento en sprint"
              color={n.breakfast_days >= 5 ? STATE_COLORS.ok : STATE_COLORS.warn}
            />
            <Kpi
              label="Días con registro"
              value={`${logged.length}`}
              unit={`/${n.window_days}`}
              note="Base de los promedios de esta sección"
            />
          </div>

          <div className="mt-4 grid gap-6 lg:grid-cols-2">
            <div>
              <p className="mb-1 text-[11.5px] text-peri-300">Calorías por día</p>
              <Bars
                values={n.daily_calories.map((d) => d.calories)}
                labels={n.daily_calories.map((d) => fmtDayNumber(d.date))}
                reference={reference}
                referenceLabel="Referencia diaria"
                colorFor={(value) =>
                  reference && value >= reference * 0.85 ? "#4274d9" : "#fe694e"
                }
                label="Calorías registradas por día"
              />
              <p className="text-[11.5px] leading-relaxed text-peri-300">
                Las barras grises son días sin registro. La línea punteada es la referencia diaria.
              </p>
            </div>

            <div>
              <p className="mb-1 text-[11.5px] text-peri-300">Distribución promedio por comida</p>
              {[
                ["Desayuno", n.per_meal.desayuno],
                ["Almuerzo", n.per_meal.almuerzo],
                ["Cena", n.per_meal.cena],
                ["Snack", n.per_meal.snack],
              ].map(([label, value]) => (
                <MeterRow
                  key={label}
                  label={label}
                  value={reference ? (value / (reference * 0.4)) * 100 : null}
                  display={value === null ? NOT_RECORDED : `${fmtNumber(value)} kcal`}
                  muted={value === null}
                />
              ))}
              <p className="text-[11.5px] leading-relaxed text-peri-300">
                Promedios contando solo lo que el deportista registró. No constituyen una
                valoración nutricional.
              </p>
            </div>
          </div>
        </>
      )}
    </Section>
  );
};

// --- wellness ----------------------------------------------------------------

export const WellnessSection = ({ report }) => {
  const w = report.athlete.wellness;
  if (!w) return null;

  const rows = [
    ["Horas de sueño", w.sleep_hours, 9, false, " h"],
    ["Calidad de sueño", w.sleep_quality, 5, false, "/5"],
    ["Esfuerzo percibido", w.rpe, 10, null, "/10"],
    ["Nivel de fatiga", w.fatigue, 10, true, "/10"],
    ["Dolor muscular", w.doms, 10, true, "/10"],
    ["Estado de ánimo", w.mood, 10, false, "/10"],
    ["Motivación", w.motivation, 10, false, "/10"],
  ];

  const hasAny = rows.some(([, value]) => value !== null);

  return (
    <Section title="Bienestar" meta="Autorreporte diario en tres pasos: sueño, carga y ánimo">
      {!hasAny ? (
        <Empty>Sin check-in registrado en el periodo.</Empty>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            {rows.map(([label, value, max, inverted, unit]) => {
              // On fatigue and soreness a LOW number is the good outcome, so the
              // bar's colour has to invert or the page would read as alarming
              // exactly when the athlete is fine.
              const filled = value === null ? 0 : (value / max) * 100;
              const goodness = inverted === null ? 60 : inverted ? 100 - filled : filled;
              return (
                <MeterRow
                  key={label}
                  label={label}
                  value={filled}
                  muted={value === null}
                  color={
                    goodness >= 70
                      ? STATE_COLORS.ok
                      : goodness >= 45
                        ? "#4274d9"
                        : STATE_COLORS.warn
                  }
                  display={value === null ? NOT_RECORDED : `${fmtNumber(value, 1)}${unit}`}
                />
              );
            })}
            <p className="mt-1.5 text-[11.5px] leading-relaxed text-peri-300">
              Promedios del periodo. En fatiga y dolor muscular, un valor bajo es mejor. Son datos
              autorreportados: sirven para ajustar la carga, no para diagnosticar.
            </p>
          </div>

          <div>
            <p className="text-[11.5px] text-peri-300">Índice de estado día a día · últimos 14 días</p>
            <Heatmap
              series={report.athlete.score_series}
              dates={report.sleep_series.map((entry) => fmtShortDate(entry.date))}
            />
            <Legend
              items={[
                ...WELLNESS_STATES.map((state) => ({ label: state.name, color: state.color })),
                { label: "Sin registro", color: STATE_COLORS.missing },
              ]}
            />

            <p className="mb-0.5 mt-4 text-[11.5px] text-peri-300">
              Horas de sueño · últimos 14 días
            </p>
            <Bars
              values={report.sleep_series.map((entry) => entry.hours)}
              labels={report.sleep_series.map((entry) => fmtDayNumber(entry.date))}
              reference={8}
              referenceLabel="8 horas"
              colorFor={(value) => (value >= 7 ? STATE_COLORS.ok : STATE_COLORS.warn)}
              height={70}
              label="Horas de sueño por noche en los últimos 14 días"
            />
          </div>
        </div>
      )}
    </Section>
  );
};

// --- consistency -------------------------------------------------------------

export const ConsistencySection = ({ report }) => {
  const a = report.athlete;

  return (
    <Section title="Consistencia" meta="Qué tan sólido es el dato que sostiene este reporte">
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Kpi
          label="Días evaluados"
          value={fmtNumber(a.sessions_recorded)}
          note={`En ${report.period.days} días de periodo`}
        />
        <Kpi
          label="Días con registro"
          value={fmtPercent(a.checkin_adherence)}
          note="Constancia del deportista en la app"
          color={
            a.checkin_adherence !== null && a.checkin_adherence >= 70
              ? STATE_COLORS.ok
              : STATE_COLORS.warn
          }
        />
        <Kpi
          label="Evolución del índice"
          value={fmtDelta(a.performance_delta)}
          note="Segunda mitad del periodo contra la primera"
          color={deltaColor(a.performance_delta)}
        />
        <Kpi
          label="Comparado con"
          value={fmtNumber(report.peers.count)}
          note={`Deportistas de ${report.peers.category}`}
        />
      </div>
      <p className="mt-3.5 text-[11.5px] leading-relaxed text-peri-300">
        Todas las métricas provienen de registros cargados en la app Athletain por el cuerpo
        técnico y por el propio deportista, no de estimaciones.
      </p>
    </Section>
  );
};

// --- recommendations / notes / contact --------------------------------------

export const RecommendationsSection = ({ report }) => (
  <Section title="Qué hacer con este reporte" meta="Reglas aplicadas sobre los datos del periodo">
    {report.recommendations.length === 0 ? (
      <Empty>Sin acciones prioritarias en este periodo.</Empty>
    ) : (
      <ol className="list-decimal space-y-2.5 pl-5">
        {report.recommendations.map((item) => (
          <li key={item.title} className="text-sm text-ink">
            <b className="text-navy-900">{item.title}:</b> {item.detail}
          </li>
        ))}
      </ol>
    )}
  </Section>
);

export const NotesSection = ({ report }) => (
  <Section title="Observaciones" meta="Se imprimen junto con el reporte">
    <label htmlFor="report-notes" className="sr-only">
      Observaciones del cuerpo técnico
    </label>
    <textarea
      id="report-notes"
      placeholder="Escribe aquí antes de exportar el PDF."
      className="min-h-[100px] w-full resize-y rounded-lg border border-neutral-200 p-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-200"
    />
    <div className="mt-6 grid gap-10 text-[12.5px] text-ink sm:grid-cols-2">
      <div className="border-t border-navy-900 pt-2">
        {report.athlete.club || "Cuerpo técnico"} · Responsable del seguimiento
      </div>
      <div className="border-t border-navy-900 pt-2">
        Athletain · Reporte {report.athlete.athlete_id.slice(0, 8)} ·{" "}
        {String(report.generated_at).slice(0, 10)}
      </div>
    </div>
  </Section>
);

export const ContactSection = ({ report }) => (
  <Section title="Siguiente paso" meta="Gestión a través del club">
    <p className="m-0 max-w-[70ch] text-sm text-ink">
      Para solicitar una prueba, un video de la última sesión o un reporte ampliado de{" "}
      {report.athlete.full_name.split(" ")[0]}, contacta a{" "}
      {report.athlete.club || "la academia"} desde la app. Toda gestión con deportistas menores de
      edad se tramita a través del club y con autorización de sus padres o representantes.
    </p>
    <div className="mt-6 grid gap-10 text-[12.5px] text-ink sm:grid-cols-2">
      <div className="border-t border-navy-900 pt-2">{report.athlete.club || "Academia"}</div>
      <div className="border-t border-navy-900 pt-2">
        Athletain · Ficha {report.athlete.athlete_id.slice(0, 8)} ·{" "}
        {String(report.generated_at).slice(0, 10)}
      </div>
    </div>
  </Section>
);
